import React, { useEffect } from 'react'
import { Button, Card, CardBody, CardTitle, Col, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import { updateAdminSettings } from 'helpers/API Calls/System & Settings/Admin Settings';

function Website({data}: any) {
  const formik = useFormik({
    initialValues: {
      businessEmail: data?.CONTACT_EMAIL?.value || '',
      officePhone: data?.OFFICE_PHONE?.value || '',
      mobilePhone: data?.MOBILE_PHONE?.value || '',
      facebookLink: data?.FACEBOOK_LINK?.value || '',
      xLink: data?.X_LINK?.value || '',
      instagramLink: data?.INSTAGRAM_LINK?.value || '',
      youtubeLink: data?.YOUTUBE_LINK?.value || '',
    },
    validationSchema: yup.object().shape({
      businessEmail: yup.string().email('Invalid email address').required('Business email is required'),
      officePhone: yup.string().matches(/^[0-9]+$/, 'Must be only digits'),
      mobilePhone: yup.string().matches(/^[0-9]+$/, 'Must be only digits'),
      facebookLink: yup.string().url('Must be a valid URL').nullable(),
      xLink: yup.string().url('Must be a valid URL').nullable(),
      instagramLink: yup.string().url('Must be a valid URL').nullable(),
      youtubeLink: yup.string().url('Must be a valid URL').nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const reqData = [
        {
          key: "CONTACT_EMAIL",
          value: values.businessEmail,
          id: data?.CONTACT_EMAIL?.id,
        },
        {
          key: "OFFICE_PHONE",
          value: values.officePhone,
          id: data?.OFFICE_PHONE?.id,
        },
        {
          key: "MOBILE_PHONE",
          value: values.mobilePhone,
          id: data?.MOBILE_PHONE?.id,
        },
        {
          key: "FACEBOOK_LINK",
          value: values.facebookLink,
          id: data?.FACEBOOK_LINK?.id,
        },
        {
          key: "X_LINK",
          value: values.xLink,
          id: data?.X_LINK?.id,
        },
        {
          key: "INSTAGRAM_LINK",
          value: values.instagramLink,
          id: data?.INSTAGRAM_LINK?.id,
        },
        {
          key: "YOUTUBE_LINK",
          value: values.youtubeLink,
          id: data?.YOUTUBE_LINK?.id,
        }
      ];
      const response = await updateAdminSettings({'settings': reqData});
      if (response) {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (data) {
      formik.setValues({
        businessEmail: data?.CONTACT_EMAIL?.value || '',
        officePhone: data?.OFFICE_PHONE?.value || '',
        mobilePhone: data?.MOBILE_PHONE?.value || '',
        facebookLink: data?.FACEBOOK_LINK?.value || '',
        xLink: data?.X_LINK?.value || '',
        instagramLink: data?.INSTAGRAM_LINK?.value || '',
        youtubeLink: data?.YOUTUBE_LINK?.value || '',
      });
    }
  }, [data]);

  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);
      
  return (
    <Card>
      <CardBody>
        <Form onSubmit={formik.handleSubmit} autoComplete="off">
          <div className="mb-4">
            <CardTitle tag="h4" className='mb-4'>Contact Information</CardTitle>
            <Row>
              <Col sm="6">
                <div className="mb-3">
                  <Label htmlFor="businessEmail">Business Email <span className="text-danger">*</span></Label>
                  <Input
                    id="businessEmail"
                    name="businessEmail"
                    type="email"
                    value={formik.values.businessEmail}
                    onChange={formik.handleChange}
                    invalid={formik.touched.businessEmail && formik.errors.businessEmail ? true : false}
                    disabled={data?.CONTACT_EMAIL?.can_update !== 1}
                  />                  {formik.errors.businessEmail && formik.touched.businessEmail && (
                    <FormFeedback type="invalid">{String(formik.errors.businessEmail)}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="officePhone">Office Phone</Label>
                  <Input
                    id="officePhone"
                    name="officePhone"
                    type="text"
                    value={formik.values.officePhone}
                    onChange={formik.handleChange}
                    invalid={formik.touched.officePhone && formik.errors.officePhone ? true : false}
                    disabled={data?.OFFICE_PHONE?.can_update !== 1}
                  />                  {formik.errors.officePhone && formik.touched.officePhone && (
                    <FormFeedback type="invalid">{String(formik.errors.officePhone)}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="mobilePhone">Mobile Phone</Label>
                  <Input
                    id="mobilePhone"
                    name="mobilePhone"
                    type="text"
                    value={formik.values.mobilePhone}
                    onChange={formik.handleChange}
                    invalid={formik.touched.mobilePhone && formik.errors.mobilePhone ? true : false}
                    disabled={data?.MOBILE_PHONE?.can_update !== 1}
                  />                  {formik.errors.mobilePhone && formik.touched.mobilePhone && (
                    <FormFeedback type="invalid">{String(formik.errors.mobilePhone)}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <div className="mb-4">
            <CardTitle tag="h4" className='mb-4'>Social Media Links</CardTitle>
            <Row>
              <Col sm="6">
                <div className="mb-3">
                  <Label htmlFor="facebookLink">Facebook Profile URL</Label>
                  <Input
                    id="facebookLink"
                    name="facebookLink"
                    type="url"
                    value={formik.values.facebookLink}
                    onChange={formik.handleChange}
                    invalid={formik.touched.facebookLink && formik.errors.facebookLink ? true : false}
                    disabled={data?.FACEBOOK_LINK?.can_update !== 1}
                  />                  {formik.errors.facebookLink && formik.touched.facebookLink && (
                    <FormFeedback type="invalid">{String(formik.errors.facebookLink)}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="xLink">X (Twitter) Profile URL</Label>
                  <Input
                    id="xLink"
                    name="xLink"
                    type="url"
                    value={formik.values.xLink}
                    onChange={formik.handleChange}
                    invalid={formik.touched.xLink && formik.errors.xLink ? true : false}
                    disabled={data?.X_LINK?.can_update !== 1}
                  />                  {formik.errors.xLink && formik.touched.xLink && (
                    <FormFeedback type="invalid">{String(formik.errors.xLink)}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col sm="6">
                <div className="mb-3">
                  <Label htmlFor="instagramLink">Instagram Profile URL</Label>
                  <Input
                    id="instagramLink"
                    name="instagramLink"
                    type="url"
                    value={formik.values.instagramLink}
                    onChange={formik.handleChange}
                    invalid={formik.touched.instagramLink && formik.errors.instagramLink ? true : false}
                    disabled={data?.INSTAGRAM_LINK?.can_update !== 1}
                  />
                  {formik.errors.instagramLink && formik.touched.instagramLink && (
                    <FormFeedback type="invalid">{String(formik.errors.instagramLink)}</FormFeedback>
                  )}
                </div>

                <div className="mb-3">
                  <Label htmlFor="youtubeLink">YouTube Channel URL</Label>
                  <Input
                    id="youtubeLink"
                    name="youtubeLink"
                    type="url"
                    value={formik.values.youtubeLink}
                    onChange={formik.handleChange}
                    invalid={formik.touched.youtubeLink && formik.errors.youtubeLink ? true : false}
                    disabled={data?.YOUTUBE_LINK?.can_update !== 1}
                  />
                  {formik.errors.youtubeLink && formik.touched.youtubeLink && (
                    <FormFeedback type="invalid">{String(formik.errors.youtubeLink)}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <div className="d-flex flex-wrap gap-2 justify-content-end">
            <Button type="submit" color="primary" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  )
}

export default Website
